import { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip, { ChipProps } from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import RuleOutlined from '@mui/icons-material/RuleOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import AssignmentIndOutlined from '@mui/icons-material/AssignmentIndOutlined';
import { Formatter } from '@omnigenbiodata/ui';
import DetailRow from '../DetailRow';

interface ParticipantConsentProps {
  participantStatus: string | null;
  withdrawalLevel: string | null;
  withdrawalTs: string | null;
  withdrawalReason: string | null;

  consent1: boolean | null;
  consent2: boolean | null;
  consent3: boolean | null;
  consent4: boolean | null;
  consent5: boolean | null;
  consent6: boolean | null;
  consent7: boolean | null;
  consent8: boolean | null;
  consent9: boolean | null;
  consentFormDate: string | null;
  consentFormVersion: string | null;
  pisConfirm: boolean | null;
  pisDate: string | null;
  pisVersion: string | null;
  videoConfirm: boolean | null;
  ehrOptIn: boolean | null;
  ancestryOptIn: boolean | null;
  communicationOptIn: boolean | null;
}

interface ConsentSectionProps {
  title: string;
  icon: ReactNode;
  children: ReactNode;
}

const withdrawnStatuses = ['WITHDRAWN', 'DECEASED', 'SUSPENDED'];

const formatDate = (value: string | null) =>
  value ? Formatter({ input: value, format: 'friendlyDate' }) : 'Not recorded';

function isWithdrawnStatus(status: string | null) {
  return Boolean(status && withdrawnStatuses.includes(status));
}

function getParticipantStatusColour(status: string | null): ChipProps['color'] {
  if (!status) {
    return 'default';
  }

  if (isWithdrawnStatus(status)) {
    return 'error';
  }

  return 'success';
}

function renderParticipantStatus(status: string | null) {
  const displayStatus = status || 'UNKNOWN';

  return (
    <Chip
      label={displayStatus}
      color={getParticipantStatusColour(displayStatus)}
      size="small"
      sx={{ fontWeight: 700 }}
    />
  );
}

function renderBooleanChip(value: boolean | null, trueLabel = 'Yes', falseLabel = 'No') {
  if (value === null) {
    return <Chip label="Not recorded" color="default" size="small" variant="outlined" sx={{ fontWeight: 700 }} />;
  }

  return (
    <Chip
      label={value ? trueLabel : falseLabel}
      color={value ? 'success' : 'default'}
      size="small"
      variant={value ? 'filled' : 'outlined'}
      sx={{ fontWeight: 700 }}
    />
  );
}

function getCardTone(participantStatus: string | null, consentAnswers: Array<boolean | null>) {
  if (isWithdrawnStatus(participantStatus)) {
    return {
      bgcolor: 'error.lighter',
      borderColor: 'error.light',
      iconColor: 'error' as const,
    };
  }

  const hasMissingConsent = consentAnswers.some((answer) => answer === null);

  if (hasMissingConsent) {
    return {
      bgcolor: 'warning.lighter',
      borderColor: 'warning.light',
      iconColor: 'warning' as const,
    };
  }

  return {
    bgcolor: 'success.lighter',
    borderColor: 'success.light',
    iconColor: 'success' as const,
  };
}

function ConsentSection({ title, icon, children }: ConsentSectionProps) {
  return (
    <Box mt={2}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1,
        }}
      >
        {icon}

        <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </Box>

      {children}
    </Box>
  );
}

function ParticipantConsent({
  participantStatus,
  withdrawalLevel,
  withdrawalTs,
  withdrawalReason,
  consent1,
  consent2,
  consent3,
  consent4,
  consent5,
  consent6,
  consent7,
  consent8,
  consent9,
  consentFormVersion,
  consentFormDate,
  pisDate,
  pisVersion,
  pisConfirm,
  videoConfirm,
  ehrOptIn,
  ancestryOptIn,
  communicationOptIn,
}: ParticipantConsentProps) {
  const consentAnswers = [consent1, consent2, consent3, consent4, consent5, consent6, consent7, consent8, consent9];

  const answeredConsentCount = consentAnswers.filter((answer) => answer !== null).length;
  const tone = getCardTone(participantStatus, consentAnswers);
  const isWithdrawn = isWithdrawnStatus(participantStatus);

  return (
    <Box mb={2}>
      <Paper
        elevation={4}
        sx={{
          bgcolor: tone.bgcolor,
          border: 1,
          borderColor: tone.borderColor,
          overflow: 'hidden',
        }}
      >
        <Box p={2}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
              gap: 1,
              mb: 1,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            >
              <FactCheckOutlined color={tone.iconColor} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" component="h2" sx={{ fontWeight: 700, m: 0 }}>
                Enrolment Consent
              </Typography>

              <Typography variant="body2" component="p" color="text.secondary">
                Consent status, documents, confirmations and optional participant choices
              </Typography>
            </Box>
          </Box>

          <ConsentSection
            title="Consent Status"
            icon={
              isWithdrawn ? (
                <PersonOffOutlined color={tone.iconColor} fontSize="small" />
              ) : (
                <AssignmentIndOutlined color={tone.iconColor} fontSize="small" />
              )
            }
          >
            <DetailRow label="Participant Status" value={renderParticipantStatus(participantStatus)} />

            {isWithdrawn && (
              <>
                <DetailRow label="Withdrawal Level" value={withdrawalLevel} />
                <DetailRow label="Withdrawal Date" value={formatDate(withdrawalTs)} />
                <DetailRow label="Withdrawal Reason" value={withdrawalReason} />
              </>
            )}

            <DetailRow
              label="Consent Completion"
              value={
                <Chip
                  label={`${answeredConsentCount}/9 answered`}
                  color={answeredConsentCount === 9 ? 'success' : 'warning'}
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              }
            />
          </ConsentSection>

          <Divider sx={{ my: 2 }} />

          <ConsentSection
            title="Consent Documents"
            icon={<DescriptionOutlined color={tone.iconColor} fontSize="small" />}
          >
            <DetailRow label="Consent Form Date" value={formatDate(consentFormDate)} />
            <DetailRow label="Consent Form Version" value={consentFormVersion} />
            <DetailRow label="PIS Date" value={formatDate(pisDate)} />
            <DetailRow label="PIS Version" value={pisVersion} />
          </ConsentSection>

          <Divider sx={{ my: 2 }} />

          <ConsentSection
            title="Participant Confirmations"
            icon={<RuleOutlined color={tone.iconColor} fontSize="small" />}
          >
            <DetailRow label="PIS Confirmed" value={renderBooleanChip(pisConfirm)} />
            <DetailRow label="Video Confirmed" value={renderBooleanChip(videoConfirm)} />
            <DetailRow label="Consent 1" value={renderBooleanChip(consent1)} />
            <DetailRow label="Consent 2" value={renderBooleanChip(consent2)} />
            <DetailRow label="Consent 3" value={renderBooleanChip(consent3)} />
            <DetailRow label="Consent 4" value={renderBooleanChip(consent4)} />
            <DetailRow label="Consent 5" value={renderBooleanChip(consent5)} />
            <DetailRow label="Consent 6" value={renderBooleanChip(consent6)} />
            <DetailRow label="Consent 7" value={renderBooleanChip(consent7)} />
            <DetailRow label="Consent 8" value={renderBooleanChip(consent8)} />
            <DetailRow label="Consent 9" value={renderBooleanChip(consent9)} />
          </ConsentSection>

          <Divider sx={{ my: 2 }} />

          <ConsentSection title="Optional Opt-Ins" icon={<TaskAltOutlined color={tone.iconColor} fontSize="small" />}>
            <DetailRow label="EHR Opt-In" value={renderBooleanChip(ehrOptIn, 'Opted in', 'Not opted in')} />
            <DetailRow label="Ancestry Opt-In" value={renderBooleanChip(ancestryOptIn, 'Opted in', 'Not opted in')} />
            <DetailRow
              label="Communications Opt-In"
              value={renderBooleanChip(communicationOptIn, 'Opted in', 'Not opted in')}
            />
          </ConsentSection>
        </Box>
      </Paper>
    </Box>
  );
}

export default ParticipantConsent;
